import config from "config/config";
import Content, { ContentNS } from "content/content";
import Header from "features/layout/components/header/Header";
import JourneySection from "features/layout/components/journey-view/JourneySection";
import JourneySectionContainer from "./components/journey-view/styles/JourneySectionContainer.styled";

import Loader from "features/shared/components/loader/Loader";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import Container from "features/shared/styles/container/Container.styled";
import Logging from "logging";
import { useEffect, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Auth from "services/auth/auth";
import DataProvider from "services/data-provider/data-provider";
import NativeBridge from "services/native-bridge/native-bridge";
import { useAppStore } from "store/store";

import usePreventZoom from "./hooks/usePreventZoom";
import LayoutContainer from "./styles/LayoutContainer.styled";
import OutletContainer from "./styles/OutletContainer.styled";
import useSessionRootRedirect from "features/shared/hooks/navigation/useSessionStartRedirect";
import { getSessionRelativeUrl } from "features/shared/hooks/navigation/useFlowRedirect";
import AnalyticsService from "services/analytics/analytics";
import useOutageInfo from "features/shared/hooks/useOutageInfo";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";
import PaxSession from "models/session/pax-session";

const Layout = () => {
  usePreventZoom();

  // if exactly at session root (ie /session/{sessionId}),
  // use default session start logic and redirect where required
  useSessionRootRedirect();

  const { locale, chosenPaxSessionIds } = useAppStore((state) => ({
    locale: state.locale,
    chosenPaxSessionIds: state.chosenPaxSessionIds,
  }));

  const { sessionId } = useParams();

  const paxSession = usePaxSession();
  const paxSessionId = paxSession?.paxSessionId || "missing";
  const paxSessions = useAppStore((state) => state.paxSessions);
  const storeSessionId = useAppStore((state) => state.sessionId);

  const navigate = useNavigateWithQuery();

  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const [loading, setLoading] = useState(true);
  const flowReadySentRef = useRef(false);
  const sessionLockedClosedRef = useRef(false);
  // Mirrors `loading` but as a ref: the async .then() below closes over
  // `loading`'s value from when the effect ran, so setLoading(true) on a
  // session change wouldn't be visible to that same closure. This ref is
  // always read fresh, so it's what actually gates the once-per-session work.
  const sessionLoadedRef = useRef(false);
  const flowLifecycleSessionIdRef = useRef<string | undefined>(undefined);

  const isJourneyShown = useAppStore((state) => state.isJourneyShown);

  const updateSession = useAppStore((state) => state.updateSession);

  const [ineligibilityReason, setIneligibilityReason] = useAppStore((state) => [
    state.ineligibilityReason,
    state.setIneligibilityReason,
  ]);

  const [sessionIsLocked, setSessionIsLocked] = useAppStore((state) => [
    state.sessionIsLocked,
    state.setSessionIsLocked,
  ]);

  const hasFinalizedChecklist = paxSessions.some((paxSession) =>
    paxSession.checklist.isFinalized()
  );

  const [searchParams] = useSearchParams();

  // demo mode
  const setDemoScenario = useAppStore((state) => state.setDemoScenario);

  // try getting outage spec if there is one
  const isOutage = useOutageInfo().isOutage;

  const hideInitialLoader = () => {
    const loader = document.getElementById("init-loader-container");
    loader?.style.setProperty("display", "none");
  };

  // TODO review session fetch in Layout
  const refreshCurrentSession = async (sessionId: string) => {
    updateSession(await DataProvider.shared.getSession(sessionId));
  };

  const logSessionFetchError = (err: any) => {
    AnalyticsService.shared.trackEvent(
      "app_error",
      {},
      false // isScanRelated
    );

    Logging.log(
      "layout",
      paxSessionId,
      `Failed to fetch session with sessionId ${sessionId}. ${
        err instanceof Error ? err.message : err
      }`
    );
  };

  const redirectNotFound = (error?: any) => {
    setLoading(false);

    if (error) {
      logSessionFetchError(error);
    }

    if (!config.isIndexLoginDissalowed) {
      navigate("/login");
    } else {
      navigate("/not-found");
    }
  };

  const onLayoutLoad = () => {
    hideInitialLoader();

    // Re-check embedded mode from current URL (after 302 redirect from /start2)
    NativeBridge.shared.refreshMode();

    const token = Auth.shared.getToken();

    if (!token) {
      redirectNotFound();
      return;
    }

    // TODO review DataProvider initialisation and session fetch in Layout
    DataProvider.init(token, locale, chosenPaxSessionIds);

    // demo scenario
    if (config.demoMode) {
      const demoScenario = searchParams.get("demo");
      if (demoScenario !== null) {
        setDemoScenario(demoScenario);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect
  useEffect(onLayoutLoad, []);

  // Track in-app navigation depth: window.history.length only ever grows
  // (it never decreases when navigating back), so it can't be used to tell
  // whether there's a prior in-app screen left to go back to. We track the
  // real back-stack depth ourselves via the router's navigation action.
  const backStackDepthRef = useRef(0);
  const previousPathnameRef = useRef<string | null>(null);
  const depthTrackingSessionIdRef = useRef<string | undefined>(undefined);
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (depthTrackingSessionIdRef.current !== sessionId) {
      // A different session than last time (Layout stayed mounted across a
      // session change) — history from the old session is meaningless here.
      backStackDepthRef.current = 0;
      previousPathnameRef.current = null;
      depthTrackingSessionIdRef.current = sessionId;
    }

    const sessionRelativeUrl = getSessionRelativeUrl(sessionId ?? "");
    const isSessionRoot = (pathname: string) =>
      pathname === sessionRelativeUrl || pathname === `${sessionRelativeUrl}/`;

    if (isSessionRoot(location.pathname)) {
      // useSessionRootRedirect immediately pushes away from here — it's
      // never a real screen to go back to.
      backStackDepthRef.current = 0;
    } else if (
      previousPathnameRef.current !== null &&
      !isSessionRoot(previousPathnameRef.current) &&
      navigationType === "PUSH"
    ) {
      // POP isn't counted here: window.history.back() is the only thing
      // that ever produces one in this app, and it's already decremented
      // synchronously in the backButton handler below. Waiting for this
      // effect's async popstate round-trip to do it would let rapid
      // repeated backButton presses race ahead of a stale counter value.
      backStackDepthRef.current += 1;
    }

    previousPathnameRef.current = location.pathname;
  }, [location, navigationType, sessionId]);

  // Listen for native backButton events (embedded mode)
  useEffect(() => {
    if (!NativeBridge.shared.isEmbedded) return;

    return NativeBridge.shared.onBackButton(() => {
      if (backStackDepthRef.current > 0) {
        backStackDepthRef.current -= 1;
        window.history.back();
      } else {
        NativeBridge.shared.closeFlow("user_closed");
      }
    });
  }, []);

  const previousPaxSessionsRef = useRef<PaxSession[]>([]);
  const previousSessionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // storeSessionId can briefly lag the route param sessionId right after
    // navigating to a new session, before the store's own session/paxSessions
    // catch up — wait until they agree so we never diff/emit using one
    // session's paxSessions under another session's id.
    if (!sessionId || storeSessionId !== sessionId) return;

    if (previousSessionIdRef.current !== sessionId) {
      // A different session than last time (Layout stayed mounted across a
      // session change) — don't diff against another session's pax data.
      previousPaxSessionsRef.current = [];
      previousSessionIdRef.current = sessionId;
    }

    NativeBridge.shared.emitDocumentAndReadyEvents(
      sessionId,
      previousPaxSessionsRef.current,
      paxSessions
    );
    previousPaxSessionsRef.current = paxSessions;
  }, [sessionId, storeSessionId, paxSessions]);

  useEffect(() => {
    if (flowLifecycleSessionIdRef.current !== sessionId) {
      // A different session than last time (Layout stayed mounted across a
      // session change) — start its flowReady/session-locked lifecycle
      // fresh instead of treating this component instance as already past it.
      flowReadySentRef.current = false;
      sessionLockedClosedRef.current = false;
      sessionLoadedRef.current = false;
      flowLifecycleSessionIdRef.current = sessionId;
      setLoading(true);
    }

    const runCheck = (isAppLoadingCall: boolean) => {
      if (isOutage) {
        // there is system outage - redirect
        setLoading(false);
        navigate("/outage");
        return;
      }

      if (ineligibilityReason) {
        if (
          ineligibilityReason === SessionIneligibilityReason.SessionIsFinalised
        ) {
          // session is finalised already
          redirectNotFound();
          return;
        }

        // ineligible session - redirect
        setLoading(false);
        navigate("/ineligible");
        return;
      }

      if (!sessionId || hasFinalizedChecklist) {
        redirectNotFound();
        return;
      }

      const sessionLoadStartTime = new Date().getTime();

      DataProvider.shared.setLocale(locale);

      refreshCurrentSession(sessionId)
        .then(() => {
          setSessionIsLocked(false);
          if (!sessionLoadedRef.current) {
            sessionLoadedRef.current = true;
            setLoading(false);

            // Emit flowReady to native shell (embedded mode)
            if (!flowReadySentRef.current) {
              flowReadySentRef.current = true;
              NativeBridge.shared.flowReady(sessionId);
            }

            if (isAppLoadingCall) {
              AnalyticsService.shared.trackEvent(
                "app_loaded",
                {
                  app_loading_duration:
                    (new Date().getTime() - sessionLoadStartTime) / 1000,
                },
                false // isScanRelated
              );
            }
          }
        })
        .catch((err) => {
          if (
            err instanceof DataProviderGeneralException &&
            err.type !== DataProviderExceptionType.Unknown &&
            err.type !== DataProviderExceptionType.BadRequest &&
            err.type !== DataProviderExceptionType.NotFound
          ) {
            // api "general" error
            switch (err.type) {
              case DataProviderExceptionType.NotProcessable:
              case DataProviderExceptionType.NotEligible:
                // not processable or not eligible
                logSessionFetchError(err);
                setIneligibilityReason(
                  SessionIneligibilityReasonFromApi(err.message)
                );
                return;

              case DataProviderExceptionType.SessionIsLocked:
                setSessionIsLocked(true);
                if (!sessionLockedClosedRef.current) {
                  sessionLockedClosedRef.current = true;
                  NativeBridge.shared.closeFlow("session_locked");
                }
                return;

              case DataProviderExceptionType.SessionIsFinalised:
                redirectNotFound();
                return;
            }
          }

          // any other error - redirect to "not found"
          redirectNotFound(err);
          return;
        });
    };

    // Set interval to run the check every 10 seconds when session is locked
    if (sessionIsLocked === true) {
      const intervalId = setInterval(() => {
        runCheck(false);
      }, 10000);

      return () => clearInterval(intervalId);
    } else {
      // Run initial check immediately if session is not locked
      runCheck(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionIsLocked,
    isOutage,
    ineligibilityReason,
    locale,
    sessionId,
    chosenPaxSessionIds,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // set consent state for Analytics
    // if consent was already set for session, we would set it here
    const withConsentResponse =
      paxSessions.filter((pax) => pax.consented !== undefined) || [];

    if (withConsentResponse.length > 0) {
      const consentStatus = withConsentResponse.every(
        (pax) => pax.consented === true
      );

      AnalyticsService.shared.setConsentStatus(consentStatus);
    }
  }, [loading, paxSessions]);

  if (sessionIsLocked === true) {
    return (
      <>
        <Header />
        <Loader title={textUI("app-loader-session-lock")} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <Loader title={textUI("app-loader-screen-prompt")} />
      </>
    );
  }

  return (
    <LayoutContainer>
      <Header />
      {isJourneyShown ? (
        <JourneySectionContainer>
          <JourneySection />
        </JourneySectionContainer>
      ) : null}
      <OutletContainer>
        <Container id="app-content">
          <Outlet />
        </Container>
      </OutletContainer>
    </LayoutContainer>
  );
};

export default Layout;
